import { Button } from './ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Trash2,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useState } from 'react';
import { ScanAPI, Scan } from '../api/scans';

interface DeleteScanProps {
  isOpen: boolean;
  onClose: () => void;
  scan: Scan | null;
  onSuccess?: () => void;
}

export function DeleteScan({ isOpen, onClose, scan, onSuccess }: DeleteScanProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!scan) return;

    setIsDeleting(true);
    setError(null);
    
    try {
      const response = await ScanAPI.delete(scan.id);

      if (response.code === '200') {
        setIsDeleting(false);
        setIsComplete(true);

     
        if (onSuccess) {
          onSuccess();
        }
        
         onClose();
          setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to delete scan');
      }
    } catch (error) {
      setIsDeleting(false);
      setError(error instanceof Error ? error.message : 'Failed to delete scan. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan Deleted Successfully!</h3>
          <p className="text-muted-foreground">
            The scan "{scan?.version}" has been permanently deleted from the system.
          </p>
        </div>
      );
    }

    if (isDeleting) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Deleting Scan...</h3>
          <p className="text-muted-foreground">Permanently removing scan from the system...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to permanently delete the scan <strong>{scan?.code}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <span>Delete Scan</span>
          </DialogTitle>
          <DialogDescription>
            This action will permanently delete the scan and cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>
        
        {!isDeleting && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
